import { cn } from '@/lib/utils';

/**
 * Title + metadata row: `{title}` on one line, `{childName · worldName ·
 * createdDate}` (opacity-50) below. Used by Inline-Player and Now-Playing
 * Full-Screen; MiniPlayer has its own compressed title-row layout (single
 * line, no date).
 *
 * Caller supplies the pre-resolved strings — no audioStore/store coupling
 * inside this primitive. Keeps it pure presentational + easy to test.
 */
interface AudioTitleMetaProps {
  title: string;
  childName: string | null;
  worldName: string;
  /** Pre-formatted via `formatDate(story.createdAt)`. Empty string hides the date slot. */
  createdDate: string;
  className?: string;
  titleClassName?: string;
  /** Optional data-testid forwarded to the title element for test queries. */
  titleTestId?: string;
}

export function AudioTitleMeta({
  title,
  childName,
  worldName,
  createdDate,
  className,
  titleClassName,
  titleTestId,
}: AudioTitleMetaProps) {
  return (
    <div className={cn('flex w-full flex-col items-center gap-2 py-4', className)}>
      <p
        data-testid={titleTestId}
        className={cn(
          'text-center text-base font-medium leading-none text-dream-white md:text-xl md:leading-5',
          titleClassName,
        )}
      >
        {title}
      </p>
      <div className="flex w-full items-start justify-center gap-2 text-xxs leading-3 text-dream-white md:text-sm md:leading-none">
        {childName && <span className="opacity-50">{childName}</span>}
        <span className="opacity-50">{worldName}</span>
        {createdDate && <span className="opacity-50">{createdDate}</span>}
      </div>
    </div>
  );
}
